import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, Copy, Check, Trash2 } from "lucide-react";
import api from "../../lib/api";

const inputCls = "w-full bg-white border border-slate-300 rounded-sm p-3 text-base focus:outline-none focus:border-orange-500";
const PLATFORMS = ["facebook", "instagram", "linkedin", "tiktok", "twitter/x"];
const TONES = ["confident", "friendly", "urgent", "educational", "humorous"];

function postText(p) {
  return `${p.hook}\n\n${p.body}\n\n${p.cta}\n\n${(p.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}`;
}

function PostCard({ p, id }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(postText(p));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white flex flex-col" data-testid={`social-post-${id}`}>
      <div className="font-semibold text-slate-900">{p.hook}</div>
      <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap flex-1">{p.body}</p>
      <p className="text-sm text-slate-900 font-semibold mt-2">{p.cta}</p>
      <div className="text-xs text-orange-600 mt-2">{(p.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}</div>
      <button onClick={copy} data-testid={`social-copy-${id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 px-2.5 py-1.5 rounded-sm hover:bg-slate-50 self-start">
        {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy post"}
      </button>
    </div>
  );
}

export default function SocialTab() {
  const [batches, setBatches] = useState([]);
  const [platform, setPlatform] = useState("facebook");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("confident");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/marketing/social").then((r) => setBatches(r.data));
  useEffect(() => {
    load();
  }, []);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/admin/marketing/social/generate", { platform, topic, tone });
      setTopic("");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (b) => {
    await api.delete(`/admin/marketing/social/${b.batch_id}`);
    load();
  };

  return (
    <div data-testid="social-tab" className="space-y-8">
      <form onSubmit={generate} className="border border-slate-200 rounded-sm p-6 bg-slate-50 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-3">
          <label className="text-sm font-medium text-slate-900 mb-1 block">Platform</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="social-platform-select" className={inputCls}>
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="md:col-span-5">
          <label className="text-sm font-medium text-slate-900 mb-1 block">Topic / angle</label>
          <input required value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="social-topic-input" placeholder="e.g. Why homeowners pick certified handymen" className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-slate-900 mb-1 block">Tone</label>
          <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="social-tone-select" className={inputCls}>
            {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button disabled={busy} data-testid="generate-social-btn" className="md:col-span-2 inline-flex justify-center items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-4 py-3 rounded-sm font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
        </button>
        {err && <div className="md:col-span-12 text-sm text-red-600" data-testid="social-error">{err}</div>}
      </form>

      {batches.length === 0 && <div className="border border-dashed border-slate-300 rounded-sm p-10 text-slate-500" data-testid="social-empty">No posts yet. Generate your first batch.</div>}
      {batches.map((b) => (
        <div key={b.batch_id} data-testid={`social-batch-${b.batch_id}`}>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-600">{b.platform}</span>
              <span className="text-xs text-slate-500 ml-3">{b.topic} · {b.tone} · {b.created_at?.slice(0, 10)}</span>
            </div>
            <button onClick={() => remove(b)} data-testid={`social-delete-${b.batch_id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            {b.posts.map((p, i) => <PostCard key={i} p={p} id={`${b.batch_id}-${i}`} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
